import { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import api from '../../../utils/api';
import { errorText } from '../../../utils/errorText';
import { displayName } from '../../../utils/userLabel';
import { STAGES } from '../../../utils/pipelineTasks';
import PipelineStatus from '../../pipeline/PipelineStatus';
import { Day } from '../cells';
import * as S from '../../specimens/styles';

/*
 * The culture pipeline of one isolate (GET /specimens/{id}/pipeline,
 * routes/pipeline_tasks.py): its five stages in order, each with its task's
 * status, who has it, what the records show, and the buttons the server
 * says this person may press (`actions`); then the handoff history, newest
 * first. Starting a stage emails the next stage's owner; handing a task
 * over emails its new owner -- the server does both, at once.
 *
 * Reopening and skipping ask why (3 characters or more); closing by hand
 * takes an optional note; handing over picks from GET /pipeline/assignable.
 */
export default function PipelineTab({ item }) {
  const { t } = useTranslation(['pipeline', 'common']);
  const [data, setData] = useState(null);
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(null);
  const [form, setForm] = useState(null);
  const [people, setPeople] = useState(null);

  const load = useCallback(() => {
    setError('');
    return api.get(`/specimens/${item.specimen_id}/pipeline`)
      .then((r) => setData(r.data))
      .catch((e) => setError({ key: e?.response?.status === 404 ? 'notStarted' : 'loadFailed' }));
  }, [item.specimen_id]);

  useEffect(() => { load(); }, [load]);

  const act = (task, kind, body) => {
    setBusy(`${task.task_id}:${kind}`);
    setError('');
    const call = kind === 'assign'
      ? api.put(`/pipeline/tasks/${task.task_id}/assignee`, body)
      : api.post(`/pipeline/tasks/${task.task_id}/${kind}`, body || {});
    return call.then(() => { setForm(null); return load(); })
      .catch((e) => setError({ key: 'actionFailed', error: errorText(e) }))
      .finally(() => setBusy(null));
  };

  const openForm = (task, kind) => {
    setForm({ taskId: task.task_id, kind, text: '', userId: task.assignee_id || '' });
    if (kind === 'assign' && people === null) {
      api.get('/pipeline/assignable').then((r) => setPeople(r.data.users || [])).catch(() => setPeople([]));
    }
  };

  const message = error ? t(error.key, { error: error.error }) : '';
  if (!data) {
    return error ? <p role="alert" style={S.error}>{message}</p>
      : <p style={S.hint}>{t('loading')}</p>;
  }

  const stages = data.stages || STAGES.map((stage) => ({ stage, task: null, signal: null }));
  const events = [...(data.events || [])].reverse();
  const name = (value) => (value ? displayName(value, t) : t('someone'));
  const first = stages[0]?.stage;

  return (
    <div data-testid="pipeline-tab">
      <p style={{ ...S.hint, marginTop: 0 }}>{t('intro')}</p>
      {error && <p role="alert" style={S.error}>{message}</p>}
      <ol className="hub-records" style={{ listStyle: 'none', paddingInlineStart: 0 }}>
        {stages.map(({ stage, task, signal }, index) => {
          const allowed = (task && data.actions?.[task.task_id]) || [];
          const open = form && task && form.taskId === task.task_id ? form : null;
          const since = task && (task.status === 'in_progress' ? task.started_at
            : ['done', 'skipped'].includes(task.status) ? task.done_at : task.ready_at);
          return (
            <li key={stage} className="hub-record" data-testid={`pipeline-stage-${stage}`}
              data-status={task?.status || ''}>
              <div className="hub-record__head">
                <strong>{index + 1}. {t(`stage.${stage}`)}</strong>
                {task && <PipelineStatus status={task.status} />}
              </div>
              {task && (
                <div style={S.hint}>
                  {task.assignee_id ? t('assignedTo', { name: name(task.assignee_name) }) : t('unassigned')}
                  {since && task.status !== 'waiting' && (
                    <> · {t(`since.${task.status}`)} <Day value={since} /></>
                  )}
                  {task.done_signal && task.status === 'done' && (
                    <> · {t(`signal.${task.done_signal}`, { defaultValue: task.done_signal })}</>
                  )}
                </div>
              )}
              {signal && task && !['done', 'skipped'].includes(task.status) && (
                <div style={S.hint} data-testid={`pipeline-signal-${stage}`}>
                  {t('recordsShow', { what: t(`signal.${signal}`, { defaultValue: signal }) })}
                </div>
              )}
              {allowed.length > 0 && !open && (
                <div className="hub-record__actions">
                  {allowed.includes('start') && (
                    <button type="button" style={smallBtn} disabled={!!busy} title={t('tip.start')}
                      data-testid={`pipeline-start-${stage}`} onClick={() => act(task, 'start')}>
                      {t('action.start')}
                    </button>
                  )}
                  {['done', 'reopen', 'skip', 'assign'].filter((k) => allowed.includes(k)).map((kind) => (
                    <button key={kind} type="button" style={smallBtn} disabled={!!busy} title={t(`tip.${kind}`)}
                      data-testid={`pipeline-${kind}-${stage}`} onClick={() => openForm(task, kind)}>
                      {t(`action.${kind}`)}
                    </button>
                  ))}
                </div>
              )}
              {open && (
                <TaskForm form={open} people={people} t={t} busy={!!busy}
                  onChange={(patch) => setForm({ ...open, ...patch })} onCancel={() => setForm(null)}
                  onSubmit={() => act(task, open.kind, open.kind === 'assign'
                    ? { user_id: open.userId || null }
                    : open.kind === 'done' ? { note: open.text.trim() || undefined } : { reason: open.text.trim() })}
                  stage={stage} />
              )}
            </li>
          );
        })}
      </ol>

      <h4 style={S.sectionTitle}>{t('history')}</h4>
      {events.length === 0 && <p style={S.hint}>{t('noHistory')}</p>}
      <ol className="hub-timeline" data-testid="pipeline-history">
        {events.map((e) => (
          <li key={e.event_id}>
            <div style={S.hint}>
              <Day value={e.created_at} /> · {t(`stage.${e.stage}`, { defaultValue: e.stage || '' })}
            </div>
            <div>{eventText(e, t, name, first)}</div>
            {humanNote(e) && (
              <div style={{ whiteSpace: 'pre-wrap', overflowWrap: 'anywhere', ...S.hint }}>{humanNote(e)}</div>
            )}
          </li>
        ))}
      </ol>
    </div>
  );
}

/* What the server writes in `note` for its own bookkeeping -- how a task was
 * assigned, which email went -- rather than words a person typed. */
const CODE_NOTES = ['default', 'backup', 'override', 'claimed', 'bulk', 'account_deleted'];

/** The words a person wrote on this event (a reason, a note), or ''. */
export function humanNote(e) {
  if (!e.note) return '';
  if (['reopened', 'skipped', 'started'].includes(e.kind)) return e.note;
  if (e.kind === 'done') return e.actor_id ? e.note : '';
  if (['assigned', 'reassigned', 'unassigned'].includes(e.kind)) return CODE_NOTES.includes(e.note) ? '' : e.note;
  return '';
}

/** One history line, from its kind and the names on it. The first stage
 * has no stage before it: its turn comes when the isolate reaches the lab. */
export function eventText(e, t, name, first) {
  const names = { actor: name(e.actor_name), from: name(e.from_user_name), to: name(e.to_user_name) };
  if (e.kind === 'created') return t(e.note === 'bulk' ? 'event.createdBulk' : 'event.created');
  if (e.kind === 'ready' && e.stage === first) return t('event.readyFirst');
  if (e.kind === 'done' && !e.actor_id) {
    return t('event.doneAuto', { what: t(`signal.${e.note}`, { defaultValue: e.note || '' }) });
  }
  return t(`event.${e.kind}`, names);
}

function TaskForm({ form, people, t, busy, onChange, onCancel, onSubmit, stage }) {
  const needsReason = form.kind === 'reopen' || form.kind === 'skip';
  const ready = needsReason ? form.text.trim().length >= 3 : true;
  return (
    <div className="hub-record__actions" style={{ flexDirection: 'column', alignItems: 'stretch', gap: 8 }}
      data-testid={`pipeline-form-${stage}`}>
      {form.kind === 'assign' ? (
        <label style={S.label}>
          {t('assignTo')}
          <select value={form.userId} onChange={(e) => onChange({ userId: e.target.value })} style={S.input}
            data-testid={`pipeline-assignee-${stage}`} disabled={people === null}>
            <option value="">{t('nobody')}</option>
            {(people || []).map((p) => (
              <option key={p.user_id} value={p.user_id}>{p.full_name || p.username}</option>
            ))}
          </select>
        </label>
      ) : (
        <label style={S.label}>
          {needsReason ? t('reason') : t('note')}
          <textarea value={form.text} onChange={(e) => onChange({ text: e.target.value })} rows={2}
            maxLength={1000} style={S.input} data-testid={`pipeline-text-${stage}`} />
        </label>
      )}
      <div style={{ display: 'flex', gap: 8 }}>
        <button type="button" style={S.btn()} disabled={busy || !ready} onClick={onSubmit}
          data-testid={`pipeline-confirm-${stage}`}>
          {form.kind === 'assign' ? t('action.save') : t('action.confirm')}
        </button>
        <button type="button" style={S.ghostBtn} onClick={onCancel}>{t('action.cancel')}</button>
      </div>
    </div>
  );
}

const smallBtn = { ...S.ghostBtn, padding: '4px 10px', fontSize: 13 };
